import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MF_URL, DF_URL, CB_URL, LC_URL } from '../constants';

function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location]);

  return (
    <>
      {/* Hero */}
      <header className="wf-hero">
        <div className="wf-container">
          <div className="wf-tag">Creative software studio</div>
          <h1>Software that grows with you.</h1>
          <p>Simple to use. Tools that get the job done. Adapts to your specific needs.</p>
        </div>
      </header>

      {/* Projects */}
      <section id="apps" className="wf-section">
        <div className="wf-container">
          <h2>Apps &amp; Book Series</h2>
          <div className="wf-grid wf-grid-3">
            <a href={LC_URL} className="wf-card" target="_blank" rel="noreferrer">
              <span className="wf-category">CRM for LINE Business</span>
              <img src="/images/ScreenLINECRM.png" alt="LINE CRM" className="wf-card-image" />
              <h3>LINE CRM</h3>
              <span className="wf-domain">linecrm.winvasion.com</span>
              <p>CRM built around LINE Business: auto-captured contacts, follow-up reminders, and inline translation for Thai, English, Chinese &amp; Japanese.</p>
              <span className="wf-link">Get started free &rarr;</span>
            </a>
            <a href={MF_URL} className="wf-card" target="_blank" rel="noreferrer">
              <span className="wf-category">Social media scheduling</span>
              <img src="/images/Gladiator.png" alt="Magnus Famous" className="wf-card-image" />
              <h3>Magnus Famous</h3>
              <span className="wf-domain">magnusfamous.com</span>
              <p>Social media command center for creators and brands.</p>
              <span className="wf-link">Launch App &rarr;</span>
            </a>
            <a href={CB_URL} className="wf-card" target="_blank" rel="noreferrer">
              <span className="wf-category">Crypto portfolio tracker</span>
              <img src="/images/CBAppScreenshot.png" alt="Ceasar's Bag" className="wf-card-image" />
              <h3>Ceasar's Bag</h3>
              <span className="wf-domain">ceasarsbag.com</span>
              <p>Crypto portfolio tracker, multi-currency &amp; bilingual.</p>
              <span className="wf-link">Free for first 50 users &rarr;</span>
            </a>
            <a href={DF_URL} className="wf-card" target="_blank" rel="noreferrer">
              <span className="wf-category">Novels</span>
              <img src="/images/Legacy2.png" alt="The Legacy of Maximus book cover" className="wf-card-image" />
              <h3>The Legacy of Maximus</h3>
              <span className="wf-domain">danielfilion.com</span>
              <p>Fantasy book series by Daniel Filion.</p>
              <span className="wf-link">Visit Site &rarr;</span>
            </a>
          </div>
        </div>
      </section>

      {/* LINE CRM */}
      <section id="linecrm" className="wf-section wf-section-alt">
        <div className="wf-container">
          <div className="wf-tag">[ LINE CRM ]</div>
          <h2>Never lose a deal on LINE again.</h2>
          <p>The CRM built around how you actually work. Every lead, every conversation, every follow-up, organized automatically from your LINE Official Account. No spreadsheets. No sticky notes. No forgotten clients.</p>
          <div className="wf-actions">
            <a href={LC_URL} className="wf-btn wf-btn-primary" target="_blank" rel="noreferrer">Start now</a>
          </div>

          <div className="wf-split">
            <div>
              <h4 className="wf-subhead">Key features</h4>
              <ul className="wf-list">
                <li>Auto-captured contacts from every LINE message</li>
                <li>Follow-up reminders sent via LINE, not email</li>
                <li>Cold leads highlighted red on your dashboard</li>
                <li>Searchable conversation history, stored forever</li>
                <li>Inline translation for Thai, English, Chinese &amp; Japanese</li>
              </ul>

              <a href={LC_URL} target="_blank" rel="noreferrer" className="wf-linecrm-badge">
                <img src="/images/linecrm-icon.svg" alt="LINE CRM" className="wf-linecrm-badge-icon" />
                <div>
                  <p className="wf-linecrm-badge-title">Winvasion <span className="wf-linecrm-badge-accent">LINE CRM</span></p>
                  <p className="wf-linecrm-badge-subtitle">Customer Relationship Management for LINE</p>
                </div>
              </a>
            </div>
            <div>
              <h4 className="wf-subhead">How it works</h4>
              <ol className="wf-list wf-list-numbered">
                <li>Connect your LINE Official Account</li>
                <li>Contacts &amp; conversations auto-capture</li>
                <li>Get reminded before a lead goes cold</li>
              </ol>
            </div>
          </div>

          <div className="wf-full-image">
            <img src="/images/ScreenLINECRM.png" alt="LINE CRM app screenshot" />
          </div>
        </div>
      </section>

      {/* Magnus Famous */}
      <section id="magnusfamous" className="wf-section">
        <div className="wf-container">
          <div className="wf-tag">[ Magnus Famous ]</div>
          <div className="wf-intro-media">
            <img src="/images/Gladiator.png" alt="Magnus Famous" className="wf-intro-image" />
            <div className="wf-intro-media-right">
              <h2>Fame doesn't happen to you. You schedule it.</h2>
              <p>Schedule posts, crush analytics, and dominate every platform, all from one place.</p>
              <div className="wf-actions">
                <a href={MF_URL} className="wf-btn wf-btn-primary" target="_blank" rel="noreferrer">Get started today</a>
                <a href={`${MF_URL}/pricing`} className="wf-btn" target="_blank" rel="noreferrer">See pricing</a>
              </div>
            </div>
          </div>

          <div className="wf-split">
            <div>
              <h4 className="wf-subhead">Key features</h4>
              <ul className="wf-list">
                <li>Schedule across platforms</li>
                <li>Multi-platform publishing</li>
                <li>Auto-updating link page (<a href="https://magnusfamous.com/links/winvasion" target="_blank" rel="noreferrer" className="wf-underline">see example</a>)</li>
                <li>AI-assisted writing</li>
                <li>Analytics dashboard</li>
              </ul>
            </div>
            <div>
              <h4 className="wf-subhead">How it works</h4>
              <ol className="wf-list wf-list-numbered">
                <li>Connect your accounts</li>
                <li>Create your content</li>
                <li>Schedule &amp; publish</li>
              </ol>
            </div>
          </div>

          <div className="wf-full-image">
            <img src="/images/MagnusFamousScreens.png" alt="Magnus Famous app screenshots" />
          </div>
        </div>
      </section>

      {/* Ceasar's Bag */}
      <section id="ceasarsbag" className="wf-section wf-section-alt">
        <div className="wf-container wf-split-media">
          <div className="wf-split-media-text">
            <div className="wf-tag">[ Ceasar's Bag ]</div>
            <h2>Your crypto portfolio. Finally organized.</h2>
            <p>Multi-currency, bilingual, and packed with context serious holders actually use.</p>
            <div className="wf-actions">
              <a href={CB_URL} className="wf-btn wf-btn-primary" target="_blank" rel="noreferrer">Get started free</a>
            </div>

            <h4 className="wf-subhead">Key features</h4>
            <ul className="wf-list">
              <li>Multi-currency support (CAD, EUR, GBP...)</li>
              <li>Full French, English &amp; Thai support</li>
              <li>Fear &amp; Greed Index on dashboard</li>
              <li>Solana epoch &amp; reward tracking</li>
              <li>Multi-portfolio organization</li>
            </ul>

            <h4 className="wf-subhead">How it works</h4>
            <ol className="wf-list wf-list-numbered">
              <li>Create your portfolio</li>
              <li>Log your transactions</li>
              <li>Read the market</li>
            </ol>
          </div>
          <div className="wf-split-media-image">
            <img src="/images/Screenshot_CeasarBag.png" alt="Ceasar's Bag app screenshot" />
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
